import React, { PureComponent } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import container from './container'
import get from 'lodash/get'
import find from 'lodash/find'
import { translations, utils } from 'gipsy-misc'
import { Icon, Checkbox, TimeInput } from 'gipsy-ui'

import Watchers from './components/Watchers'
import {
  AllDayEventBadge,
  WhenBadge,
  PinDateBadge,
  PinTimeBadge,
  DeadlineBadge,
  ETABadge,
  CompletionTimeBadge,
  NewBadge,
  RecurringBadge,
  ReviewBadge,
  OpenTaskLabel,
  Tag,
  OldTaskBadge
} from './components/badges'
import InlineComment from 'features/list/components/inlineComment'
import InlineReactions from './components/InlineReactions'
import { getMostRecentNotification } from 'features/notifications/utils'
import { styles } from 'gipsy-misc'

const TodoCheckboxOrCalendar = ({ item, onComplete }) =>
  item.type === 'event' ? (
    <CalendarIcon className="calendar" size={18} icon="Calendar" />
  ) : (
    <Checkbox
      id={item.id}
      extraSpace={7}
      offsetTop={2}
      paramName="completed"
      checked={!!item.completed}
      onChange={onComplete}
    />
  )

const CalendarIcon = styled(Icon)`
  margin-left: 3px;
  margin-top: 5px;
`

const ReviewIcon = styled(Icon)`
  margin-left: 2px;
`

const ReviewStar = ({ isCompleted }) =>
  isCompleted ? (
    <ReviewIcon style={{ top: 4 }} fill={styles.colors.textDarkColor} className="review-star" icon={'Star'} />
  ) : (
    <ReviewIcon
      style={{ top: 4 }}
      fill={'none'}
      stroke={styles.colors.darkGrey}
      className="review-star"
      icon={'StarOutlined'}
    />
  )

const Hourglass = () => <ReviewIcon style={{ top: 4 }} className="hourglass" icon="Hourglass" />

class Line extends PureComponent {
  static propTypes = {
    session: PropTypes.object.isRequired,
    item: PropTypes.object.isRequired,
    notificationsType: PropTypes.array,
    notifications: PropTypes.array,
    highlight: PropTypes.bool,
    provided: PropTypes.object,
    onClick: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
    isDraggable: PropTypes.bool,
    hideDraggableCell: PropTypes.bool,
    isDragging: PropTypes.bool,
    showWhenDate: PropTypes.bool,
    isInOnboarding: PropTypes.bool,
    group: PropTypes.string
  }

  state = {
    ...utils.badges.getBadgesTimeDataForTask(this.props.item),
    ...utils.badges.getBadgesTimeDataForEvent(this.props.item)
  }

  componentDidMount() {
    this.timer = setInterval(() => {
      const nextState = {
        ...utils.badges.getBadgesTimeDataForTask(this.props.item),
        ...utils.badges.getBadgesTimeDataForEvent(this.props.item)
      }

      /* PureComponent make a shallow comparison here to check if it needs to rerender or not, most of the time it doesnt */
      this.setState(nextState)
    }, 3 * 1000)
  }

  componentWillUnmount = () => {
    clearInterval(this.timer)
  }

  getCommentToDisplay = () => {
    const { notifications, item } = this.props
    if (!item.comments || !notifications) {
      return null
    }
    const newCommentNotifs = notifications.filter(n => n.type === 'newComment')
    const mostRecentNotif = getMostRecentNotification(newCommentNotifs)
    if (!mostRecentNotif) {
      return null
    }
    const out = (mostRecentNotif && find(item.comments, ['id', mostRecentNotif.commentId])) || get(item, 'comments[0]')
    return out
  }

  onChangeTimeInput = ({ paramName, value }) => {
    const { onChange, item } = this.props

    const data = {
      paramName,
      value: utils.datetime.getNanosecondsFromHourAndMinute({
        hour: value.hour,
        minute: value.minute
      })
    }

    onChange(item.id, data)
  }

  onChangeReaction = data => {
    const { onChange, item } = this.props
    onChange(item.id, data)
  }

  render() {
    const {
      provided = {},
      onClick,
      item,
      notificationsType,
      highlight,
      onComplete,
      showWhenDate,
      hideDraggableCell,
      isDraggable,
      isDragging,
      session,
      isInOnboarding,
      group
    } = this.props
    /* task data */
    const { isDeadlinePast, isPinDatePast, isPinTimePast } = this.state
    const {
      pinTime,
      pinDate,
      duration,
      when,
      ETATime,
      completionTime,
      shouldShowWatcher,
      dueDate,
      reviewLabel,
      reviewBadgeShouldBeBeforeTitle,
      tags,
      shouldShowOldTaskBadge
    } = utils.badges.getBadgesDataForTask(item)

    const { isReviewTask, isReviewer, reviewStatus, hasReviewerWithStatusPending } = utils.task.getTaskDataForUser(
      item,
      session
    )

    /* event data */
    const { isStartingSoon, isInProgress, diffMinutes } = this.state
    const { isAllDay } = utils.badges.getBadgesDataForEvent(item)

    const isCompleted = item.completed === 1
    const showHourglass =
      isCompleted &&
      ((!isReviewer && hasReviewerWithStatusPending) || (isReviewer && reviewStatus !== utils.reviewstatus.reviewed))
    const showInlineComment = notificationsType.includes('newComment')
    const commentToDisplay = this.getCommentToDisplay()
    const showNewBadge = notificationsType.includes('newTaskWatched') || notificationsType.includes('newTaskAssigned')
    const showHighlight = highlight
    const pulsateWatchers = notificationsType.includes('newTaskAssigned')
    const showBold = notificationsType.filter(type => type !== utils.notifications.reorderTaskType).length > 0
    const isTodayGroup = utils.datetime.isGroupToday(group)
    const isTomorrowGroup = utils.datetime.isGroupTomorrow(group)
    const isUpdatesGroup = utils.datetime.isGroupUpdates(group)
    let className =
      'line-container' +
      (isDraggable ? ' draggable' : '') +
      (isDragging ? ' dragging' : '') +
      (isCompleted ? ' completed' : '') +
      (item.type === 'event' ? ' event-line' : '') +
      (showHighlight ? ' highlight' : '') +
      (showBold ? ' bold' : '') +
      (isInOnboarding ? ' inOrboarding' : '')

    const { hour, minute } = utils.datetime.getHourAndMinuteFromNanoseconds(item.estimatedTime)

    return (
      <div
        id={item.id}
        ref={provided ? provided.innerRef : null}
        onClick={onClick}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={className}>
        <div className="line">
          {!hideDraggableCell && (
            <div className="cell draggable">
              {isDraggable ? (
                <DragIcon className="drag-handle" icon="DragHandle" isDragging={isDragging} size={16} />
              ) : (
                ''
              )}
            </div>
          )}
          <div className="cell checkbox">
            {isReviewTask ? (
              <ReviewStar isCompleted={isCompleted} />
            ) : showHourglass ? (
              <Hourglass />
            ) : (
              <TodoCheckboxOrCalendar item={item} onComplete={onComplete} />
            )}
          </div>
          <div className="cell title">
            {reviewLabel && reviewBadgeShouldBeBeforeTitle && <ReviewBadge text={reviewLabel} />}
            <span className="text">{item.title || translations.task.new}</span>
            {showNewBadge && <NewBadge />}
            {shouldShowWatcher && <Watchers watchers={item.watchers} pulsate={pulsateWatchers} />}
            {reviewLabel && !reviewBadgeShouldBeBeforeTitle && <ReviewBadge text={reviewLabel} />}
            {isAllDay && <AllDayEventBadge />}
            {showWhenDate && !pinDate && !pinTime && when && <WhenBadge when={when} />}
            {item.recurrencyInformation && <RecurringBadge />}
            {!isAllDay && pinTime && (
              <PinTimeBadge
                item={item}
                isPinTimePast={isPinTimePast}
                pinTime={pinTime}
                isCompleted={isCompleted}
                shouldDisplayText={!ETATime}
                isStartingSoon={isStartingSoon}
                isInProgress={isInProgress}
                diffMinutes={diffMinutes}
              />
            )}
            {!isAllDay && pinDate && (
              <PinDateBadge
                item={item}
                isPinDatePast={isPinDatePast}
                pinDate={pinDate}
                isCompleted={isCompleted}
                shouldDisplayText={
                  !ETATime && !utils.datetime.isToday(item.pin.date) && !utils.datetime.isTomorrow(item.pin.date)
                }
              />
            )}
            {dueDate && <DeadlineBadge item={item} isDeadlinePast={isDeadlinePast} isCompleted={isCompleted} />}
            {item.reactions && (
              <InlineReactions
                reactions={item.reactions}
                sessionId={session.id}
                itemId={item.id}
                onChange={this.onChangeReaction}
              />
            )}
            <div className="inline additionnal">
              {(isTodayGroup || isTomorrowGroup || isUpdatesGroup) && shouldShowOldTaskBadge && (
                <OldTaskBadge creationTime={item.creationTime} />
              )}
              {ETATime && <ETABadge text={ETATime} />}
              {completionTime && <CompletionTimeBadge text={completionTime} />}
            </div>
          </div>
          {tags && tags.sort(utils.tags.compareTags).map(tag => <Tag key={tag.id} {...tag} />)}
          <div className="cell additionnal">
            {(isTodayGroup || isTomorrowGroup || isUpdatesGroup) && shouldShowOldTaskBadge && (
              <OldTaskBadge creationTime={item.creationTime} />
            )}
            {ETATime && <ETABadge text={ETATime} />}
            {completionTime && <CompletionTimeBadge text={completionTime} />}
          </div>
          {isInOnboarding && (
            <div className="cell open-task">
              <OpenTaskLabel />
            </div>
          )}
          <div className="hover-button">
            <div className={item.type !== 'event' ? 'hover-button--off' : 'button--on'}>
              <div className="cell duration">
                <span>
                  <span>{duration}</span>
                </span>
              </div>
            </div>
            <div className={item.type !== 'event' ? 'hover-button--on' : 'none'}>
              <TimeInput
                showIncrementButtons={true}
                showPeriod={false}
                paramName="estimatedTime"
                hour={hour}
                minute={minute}
                onChange={this.onChangeTimeInput}
              />
            </div>
          </div>
        </div>

        {showInlineComment && commentToDisplay && (
          <InlineComment comment={commentToDisplay} taskId={item.id} uploads={item.uploads} />
        )}
      </div>
    )
  }
}

const DragIcon = styled(Icon)`
  display: ${props => (props.isDragging ? 'block' : 'none')};
`

export default container(Line)



// WEBPACK FOOTER //
// ./src/features/list/components/line/index.js